/**
 * Utilidad para disparar eventos de Meta Pixel (fbq) de forma segura.
 *
 * Todas las funciones son no-ops silenciosos si:
 *  - `window` no está definido (SSR)
 *  - `window.fbq` no está disponible después de N reintentos (AdBlocker, script lento)
 */

type FbqEventParams = Record<string, string | number | boolean | undefined | null>;

const RETRY_DELAY_MS = 500;
const MAX_RETRIES = 3;

/**
 * Dispara un evento estándar de Meta Pixel con mecanismo de reintentos.
 * Si `window.fbq` aún no cargó, reintenta cada 500ms hasta MAX_RETRIES veces.
 *
 * @param eventName  Nombre del evento estándar (ej: 'Purchase', 'InitiateCheckout')
 * @param params     Parámetros adicionales del evento (ej: { value, currency })
 * @param retries    Intentos restantes (uso interno)
 */
export function trackEvent(eventName: string, params?: FbqEventParams, retries = MAX_RETRIES): void {
    if (typeof window === 'undefined') return;

    if (typeof window.fbq === 'function') {
        window.fbq('track', eventName, params);
    } else if (retries > 0) {
        setTimeout(() => {
            trackEvent(eventName, params, retries - 1);
        }, RETRY_DELAY_MS);
    } else {
        console.warn(`[MetaPixel] fbq no disponible tras ${MAX_RETRIES} intentos para el evento "${eventName}"`);
    }
}

/**
 * Dispara el evento estándar `Purchase`.
 * Debe llamarse ÚNICAMENTE cuando el SSE/backend confirma el pago como exitoso.
 *
 * @param value     Monto total de la compra (número)
 * @param currency  Código ISO de la moneda (por defecto 'ARS')
 */
export function trackPurchase(value: number, currency = 'ARS', transactionId?: string): void {
    trackEvent('Purchase', { value, currency });
    
    // GTM (Google Tag Manager) integration
    if (typeof window !== 'undefined') {
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        if (transactionId) {
            w.dataLayer.push({
                event: 'purchase',
                value: value,
                currency: currency,
                transaction_id: transactionId
            });
        }
    }
}

/**
 * Dispara el evento estándar `InitiateCheckout`.
 * Debe llamarse cuando el usuario visualiza el formulario de pago.
 */
export function trackInitiateCheckout(): void {
    trackEvent('InitiateCheckout');
}
